"use client";

import Button from "@/components/ui/Button";
import { Loading } from "@/components/ui/Loading";
import { dashboardTitle, explainMovieUrl } from "@/constants";
import { useAuthContext } from "@/contexts/AuthContext";
import { auth } from "@/firebase";
import { useToast } from "@/hooks/useToast";
import { sendEmailVerification } from "firebase/auth";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

export default function Home() {
  const { user, isLoading, refreshUser } = useAuthContext();
  const router = useRouter();
  const { toast, Toaster } = useToast();
  const [isResendingEmail, setIsResendingEmail] = useState(false);

  // ページ表示時にユーザー情報を更新
  useEffect(() => {
    if (user && !user.emailVerified) {
      auth.currentUser?.reload().then(() => {
        // refreshUser を別で実行
        refreshUser();
      });
    }
  }, []);

  // ログイン状態に応じたリダイレクト
  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        router.push("/signin");
      } else if (user.emailVerified) {
        router.push(user.admin ? "/admin/dashboard" : "/dashboard");
      }
    }
  }, [user, isLoading]);

  // isLoading が true の場合はローディング画面を表示
  if (isLoading) return <Loading />;

  // user が null の場合はローディング画面を表示
  if (!user) return <Loading />;

  // メール確認済みの場合はリダイレクトを待つ
  if (user.emailVerified) return <Loading />;

  // メール未確認の場合のみ、このページを表示
  return (
    <>
      <Toaster />
      <div className="container mx-auto max-w-md px-4 py-8">
        {explainMovieUrl && (
          <div className="aspect-video mb-4">
            <iframe
              className="w-full h-full"
              src={explainMovieUrl}
              title={dashboardTitle}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            />
          </div>
        )}
        <h3
          className={`text-center text-base font-bold mb-8 whitespace-pre-line ${
            explainMovieUrl ? "my-2" : "mt-6"
          }`}
        >
          {dashboardTitle}
        </h3>

        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-center text-red-500 font-bold mb-4">
            Eメールの確認が完了していません
          </p>
          <p className="mb-4">
            登録時に入力したアドレスに届いたメールに記載されているリンクにアクセスしてください。
            <br />
            既にアクセスしている場合、このページを再読み込みしてください。
          </p>
          <div className="space-y-4">
            <div className="mb-8">
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => window.location.reload()}
              >
                再読み込みする
              </Button>
            </div>
            <Button
              variant="secondary"
              className="w-full"
              disabled={isResendingEmail}
              onClick={async () => {
                if (auth.currentUser) {
                  setIsResendingEmail(true);
                  try {
                    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || window.location.origin;
                    const continueUrl = `${baseUrl.replace(/\/$/, "")}/auth-action`;

                    const actionCodeSettings = {
                      // Firebaseで認証が完了した後に自動で飛ばしたいNext.jsのURL
                      url: continueUrl,
                      // trueにすることで、Firebaseのデフォルト画面を表示せず、
                      // 即座に上記のcontinueUrlへ自動リダイレクトさせます
                      handleCodeInApp: true,
                    };
                    // 第2引数に actionCodeSettings を渡す
                    await sendEmailVerification(auth.currentUser, actionCodeSettings);
                    toast({
                      title: `確認メールを${auth.currentUser.email}に再送しました`,
                      variant: "default",
                      duration: 3000,
                    });
                  } catch (error) {
                    toast({
                      title:
                        "確認メールの再送に失敗しました。時間をおいて再度お試しください。",
                      variant: "destructive",
                      duration: 3000,
                    });
                  } finally {
                    setIsResendingEmail(false);
                  }
                }
              }}
            >
              {isResendingEmail ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-600 border-t-transparent" />
                  処理中...
                </span>
              ) : (
                "確認メールを再送する"
              )}
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              onClick={async () => {
                await auth.signOut();
                router.push("/signin");
              }}
            >
              ログアウトする
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
